import { supabase } from "@/lib/supabaseClient";
import { fetchCurrentProfile } from "@/services/auth";
import { deleteMediaByUrl } from "@/services/mediaUpload";

// ---------------------------------------------------------------------------
// Módulos (agrupan los videos de la comunidad; el admin puede crear más)
// ---------------------------------------------------------------------------

export type CommunityModule = { id: string; title: string; description: string; icon: string; sortOrder: number; active: boolean };

type ModuleRow = { id: string; title: string; description: string | null; icon: string; sort_order: number; active: boolean };

const DEFAULT_MODULES: { title: string; icon: string }[] = [
  { title: "Crecimiento personal", icon: "ri-plant-line" },
  { title: "Finanzas personales", icon: "ri-money-dollar-circle-line" },
  { title: "Oratoria efectiva", icon: "ri-mic-line" },
  { title: "Modelos de negocio", icon: "ri-rocket-line" },
  { title: "Inteligencia Artificial", icon: "ri-robot-2-line" },
  { title: "Liderazgo y comunidad", icon: "ri-group-line" },
];

function mapModule(r: ModuleRow): CommunityModule {
  return { id: r.id, title: r.title, description: r.description ?? "", icon: r.icon, sortOrder: r.sort_order, active: r.active };
}

export async function fetchModules(): Promise<CommunityModule[]> {
  const { data, error } = await supabase
    .from("community_modules")
    .select("id, title, description, icon, sort_order, active")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as ModuleRow[]).map(mapModule);
}

/** Si la organización es nueva y no tiene módulos, crea los de los cursos (solo lo hace un admin). */
export async function ensureDefaultModules(): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) return;
  const { error } = await supabase.from("community_modules").insert(
    DEFAULT_MODULES.map((m, i) => ({ organization_id: profile.organizationId, title: m.title, icon: m.icon, sort_order: i + 1 }))
  );
  if (error) throw error;
}

export async function createModule(input: { title: string; description: string; icon: string }, sortOrder: number): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const { error } = await supabase.from("community_modules").insert({
    organization_id: profile.organizationId,
    title: input.title,
    description: input.description || null,
    icon: input.icon,
    sort_order: sortOrder,
  });
  if (error) throw error;
}

export async function updateModule(id: string, patch: Partial<{ title: string; description: string; icon: string; active: boolean; sortOrder: number }>): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.description !== undefined) row.description = patch.description || null;
  if (patch.icon !== undefined) row.icon = patch.icon;
  if (patch.active !== undefined) row.active = patch.active;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  const { error } = await supabase.from("community_modules").update(row).eq("id", id);
  if (error) throw error;
}

/** Borra el módulo, sus videos y los archivos que estaban en Cloudflare. */
export async function deleteModule(id: string, videos: CommunityVideo[]): Promise<void> {
  const { error } = await supabase.from("community_modules").delete().eq("id", id);
  if (error) throw error;
  await Promise.all(videos.flatMap((v) => [deleteMediaByUrl(v.videoUrl), deleteMediaByUrl(v.coverLandscapeUrl), deleteMediaByUrl(v.coverPortraitUrl)]));
}

// ---------------------------------------------------------------------------
// Videos
// ---------------------------------------------------------------------------

export type VideoLayout = "landscape" | "portrait";

export type CommunityVideo = {
  id: string;
  moduleId: string;
  title: string;
  description: string;
  videoUrl: string;
  coverLandscapeUrl: string;
  coverPortraitUrl: string;
  layout: VideoLayout;
  featured: boolean;
  durationSeconds: number | null;
  sortOrder: number;
  published: boolean;
  createdAt: string;
};

type Row = {
  id: string;
  module_id: string | null;
  title: string;
  description: string | null;
  video_url: string | null;
  cover_landscape_url: string | null;
  cover_portrait_url: string | null;
  layout: string;
  featured: boolean;
  duration_seconds: number | null;
  sort_order: number;
  published: boolean;
  created_at: string;
};

const SELECT = "id, module_id, title, description, video_url, cover_landscape_url, cover_portrait_url, layout, featured, duration_seconds, sort_order, published, created_at";

function mapRow(r: Row): CommunityVideo {
  return {
    id: r.id,
    moduleId: r.module_id ?? "",
    title: r.title,
    description: r.description ?? "",
    videoUrl: r.video_url ?? "",
    coverLandscapeUrl: r.cover_landscape_url ?? "",
    coverPortraitUrl: r.cover_portrait_url ?? "",
    layout: r.layout === "portrait" ? "portrait" : "landscape",
    featured: r.featured,
    durationSeconds: r.duration_seconds,
    sortOrder: r.sort_order,
    published: r.published,
    createdAt: r.created_at,
  };
}

/** Los socios solo reciben los publicados (lo garantiza RLS); el admin ve todos. */
export async function fetchCommunityVideos(): Promise<CommunityVideo[]> {
  const { data, error } = await supabase.from("community_videos").select(SELECT).order("sort_order", { ascending: true }).order("created_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as Row[]).map(mapRow);
}

export type VideoInput = {
  moduleId: string;
  title: string;
  description: string;
  videoUrl: string;
  coverLandscapeUrl: string;
  coverPortraitUrl: string;
  layout: VideoLayout;
  featured: boolean;
  durationSeconds: number | null;
  published: boolean;
};

function toRow(i: VideoInput) {
  return {
    module_id: i.moduleId,
    title: i.title,
    description: i.description || null,
    video_url: i.videoUrl,
    cover_landscape_url: i.coverLandscapeUrl || null,
    cover_portrait_url: i.coverPortraitUrl || null,
    layout: i.layout,
    featured: i.featured,
    duration_seconds: i.durationSeconds,
    published: i.published,
  };
}

export async function createCommunityVideo(input: VideoInput, sortOrder: number): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const { error } = await supabase.from("community_videos").insert({ organization_id: profile.organizationId, sort_order: sortOrder, ...toRow(input) });
  if (error) throw error;
}

export async function saveCommunityVideo(id: string, input: VideoInput): Promise<void> {
  const { error } = await supabase.from("community_videos").update({ ...toRow(input), updated_at: new Date().toISOString() }).eq("id", id);
  if (error) throw error;
}

export async function patchCommunityVideo(id: string, patch: Partial<{ published: boolean; featured: boolean; sortOrder: number }>): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.published !== undefined) row.published = patch.published;
  if (patch.featured !== undefined) row.featured = patch.featured;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  const { error } = await supabase.from("community_videos").update(row).eq("id", id);
  if (error) throw error;
}

export async function deleteCommunityVideo(v: CommunityVideo): Promise<void> {
  const { error } = await supabase.from("community_videos").delete().eq("id", v.id);
  if (error) throw error;
  await Promise.all([deleteMediaByUrl(v.videoUrl), deleteMediaByUrl(v.coverLandscapeUrl), deleteMediaByUrl(v.coverPortraitUrl)]);
}
